/**
 * Helpers for the "remaining from today" view of a role's monthly resource budget.
 *
 * The Raise Resource Request drawer prefills Hours Required with the budget that is
 * still ahead of the requester (the current month in full, plus every later budget
 * month in full) and limits the desired start date picker to the window between today
 * and the end of the last budgeted month.
 */

export interface MonthlyBudgetEntry {
  /** Month key in YYYY-MM format */
  month: string;
  budgetHours: number;
}

/** Strips the time component so date-only comparisons are stable. */
export function toDateOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Budgeted hours from `from` to the end of the budget: the whole of the current month
 * and of every month after it. Months before the current one are ignored.
 */
export function remainingBudgetHours(
  entries: MonthlyBudgetEntry[],
  from: Date,
): number {
  const currentMonth = monthKey(from);

  return entries.reduce((total, entry) => {
    if (entry.month < currentMonth) return total;
    return total + entry.budgetHours;
  }, 0);
}

/**
 * Hours from a YYYY-MM → hours breakdown that fall in the month of `from` or later,
 * matching the window `remainingBudgetHours` uses. Returns undefined when no breakdown
 * is available, so callers can fall back to a whole-role total.
 */
export function hoursFromMonth(
  hoursByMonth: Record<string, number> | undefined,
  from: Date,
): number | undefined {
  if (!hoursByMonth) return undefined;

  const currentMonth = monthKey(from);

  return Object.entries(hoursByMonth).reduce(
    (total, [month, hours]) =>
      month < currentMonth ? total : total + (Number.isFinite(hours) ? hours : 0),
    0,
  );
}

/** Last day of the latest month present in the breakdown, or undefined when empty. */
export function lastBudgetDay(entries: MonthlyBudgetEntry[]): Date | undefined {
  const last = entries.reduce<string | undefined>(
    (latest, entry) => (!latest || entry.month > latest ? entry.month : latest),
    undefined,
  );
  if (!last) return undefined;

  const [year, month] = last.split('-').map(Number);
  if (!year || !month) return undefined;
  return new Date(year, month, 0);
}

/** Tolerance for the float error the monthly curve accumulates. */
const REMAINING_HOURS_EPSILON = 0.05;

/**
 * First day of the earliest budget month (current month or later) that still has budget
 * left after the bookings and open requests already placed in it, or undefined when every
 * remaining month is fully committed. Never returns a day before `today`.
 */
export function firstMonthWithRemainingBudget(
  monthlyBudget: MonthlyBudgetEntry[],
  scheduledHoursByMonth: Record<string, number> | undefined,
  requestedHoursByMonth: Record<string, number> | undefined,
  today: Date,
): Date | undefined {
  const currentMonth = monthKey(today);
  const hoursIn = (hoursByMonth: Record<string, number> | undefined, month: string) => {
    const hours = hoursByMonth?.[month];
    return Number.isFinite(hours) ? (hours as number) : 0;
  };

  const candidate = [...monthlyBudget]
    .sort((a, b) => a.month.localeCompare(b.month))
    .find((entry) => {
      if (entry.month < currentMonth) return false;
      const remaining =
        entry.budgetHours -
        hoursIn(scheduledHoursByMonth, entry.month) -
        hoursIn(requestedHoursByMonth, entry.month);
      return remaining > REMAINING_HOURS_EPSILON;
    });
  if (!candidate) return undefined;

  const [year, month] = candidate.month.split('-').map(Number);
  if (!year || !month) return undefined;

  const firstOfMonth = new Date(year, month - 1, 1);
  const dateOnlyToday = toDateOnly(today);
  return firstOfMonth < dateOnlyToday ? dateOnlyToday : firstOfMonth;
}

/**
 * Even spread of `totalHours` across the months between `start` and `end` inclusive.
 * Used as a fallback when no monthly breakdown is available (demo/tour mode, or a role
 * budget saved without a monthly curve).
 */
export function evenMonthlySpread(
  totalHours: number,
  start: Date,
  end: Date,
): MonthlyBudgetEntry[] {
  const first = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  if (last < first || totalHours <= 0) return [];

  const months: string[] = [];
  for (const cursor = new Date(first); cursor <= last; cursor.setMonth(cursor.getMonth() + 1)) {
    months.push(monthKey(cursor));
  }

  const perMonth = totalHours / months.length;
  return months.map((month) => ({ month, budgetHours: perMonth }));
}
